import { useState } from 'react';

import { Logo } from '@/components/brand/Logo';
import { RecoveryCodeList, TotpSetupFlow } from '@/components/settings/TotpSetupFlow';
import { Button } from '@/components/ui/button';
import { api, type Me } from '@/lib/api';

/**
 * 二段階認証が必須のテナントで、いまのセッションが第 2 要素を通していない
 * ときに出す画面。
 *
 * **Routes の手前で出す** (招待の受諾・サインアップの確認と同じ形)。
 * `AppLayout` の下に置くと、サイドバーもお知らせも 403 になった画面を
 * 見せることになる。ここから叩くのは `/api/auth/*` だけなので、
 * 関門の中でもすべて通る。
 *
 * Passkey を登録している人には「ログインし直す」導線を出す。Passkey で
 * ログインすれば (ユーザー検証まで通れば) その時点で条件を満たすので、
 * 認証アプリを増やさずに済む。
 */
export function MfaEnrollmentPage({ me, onDone }: { me: Me; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  /*
   * リカバリーコードは有効化の応答にしか出ない。ここで `onDone()` を
   * すぐ呼ぶと画面ごと差し替わって平文が流れてしまうので、控えるための
   * 一手 (「続ける」) を挟む。
   */
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  async function signOut() {
    setBusy(true);
    try {
      await api.post('/api/auth/logout');
    } finally {
      setBusy(false);
      onDone();
    }
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-surface px-6 py-16">
      <div className="flex w-full max-w-lg flex-col gap-6">
        <Logo height={28} className="text-brand-700" />

        <div className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
          <h1 className="text-base font-bold">二段階認証の設定が必要です</h1>
          <p className="text-xs leading-relaxed text-ink-muted">
            {`${me.tenant.name} では二段階認証が必須になっています。設定を終えるまで、この画面以外は開けません。`}
          </p>

          {me.passkeyCount > 0 && recoveryCodes === null ? (
            <p className="text-xs leading-relaxed text-ink-subtle">
              {
                'Passkey を登録済みです。Passkey でログインし直せば、認証アプリを設定しなくてもそのまま利用を再開できます。'
              }
            </p>
          ) : null}

          {recoveryCodes ? (
            <>
              <RecoveryCodeList codes={recoveryCodes} />
              <p className="text-xs leading-relaxed text-ink-subtle">
                {
                  '認証アプリを失くしたときはこのコードでログインできます。控えてから次に進んでください。'
                }
              </p>
              <div>
                <Button variant="primary" onClick={onDone}>
                  控えたので続ける
                </Button>
              </div>
            </>
          ) : (
            <TotpSetupFlow onEnabled={setRecoveryCodes} />
          )}
        </div>

        {recoveryCodes === null ? (
          <div className="flex items-center gap-4">
            <Button variant="ghost" disabled={busy} onClick={signOut}>
              {me.passkeyCount > 0 ? 'ログアウトして Passkey で入り直す' : 'ログアウト'}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
